import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash';
import { IPickerCroppingRecord } from '../../../interfaces/cropping-records/picker-cropping-record.model';
import { ITradeCroppingRecord } from '../../../interfaces/trades/trade-cropping-record.model';
import { ITradeHeader } from '../../../interfaces/trades/trade-header.model';
import { EditTradeCroppingRecordsFormComponent } from '../../../shared/forms/trades/edit-trade-cropping-records-form/edit-trade-cropping-records-form.component';
import { ISelectList } from '../../../shared/models/select-list.model';
import { FieldSelectorPipe } from '../../../shared/pipes/field-selector.pipe';
import { CroppingRecordsService } from '../../../shared/services/cropping-records/cropping-records.service';
import { MessageService } from '../../../shared/services/message/message.service';
import { SeasonService } from '../../../shared/services/season/season.service';
import { TradeCroppingRecordsService } from '../../../shared/services/trades/trade-cropping-records.service';
import { UserService } from '../../../shared/services/user/user.service';
import { GatekeeperPickerModalComponent } from '../../picker-modal.component';

@Component({
  selector: 'gk-edit-trade-cropping-records-modal',
  templateUrl: './trade-cropping-records-picker.html'
})
export class TradeCroppingRecordsPickerComponent extends GatekeeperPickerModalComponent<TradeCroppingRecordsPickerComponent> {
  @ViewChild(EditTradeCroppingRecordsFormComponent, { static: true })
  tradeCroppingRecordsForm: EditTradeCroppingRecordsFormComponent;

  filterText: string;

  tradeStockPeriod: number;
  businessId: number;
  header: ITradeHeader;
  selectedCroppingRecords: ITradeCroppingRecord[] = [];
  croppingRecords: IPickerCroppingRecord[] = [];
  filteredCroppingRecords: IPickerCroppingRecord[] = [];
  hideCurrentCrop = true;

  selectedBusinessIds: Array<number>;
  selectedFieldGroupTagIds: Array<number>;
  selectedLandUses: Array<number>;
  selectedCropTypeIds: Array<number>;
  selectedCropVarietyIds: Array<number>;
  selectedTagIds: Array<number>;

  constructor(
    private tradeCroppingRecordsService: TradeCroppingRecordsService,
    private croppingRecordsService: CroppingRecordsService,
    private messageService: MessageService,
    private userService: UserService,
    private seasonService: SeasonService,
    private fieldSelectorPipe: FieldSelectorPipe,
    public dialogRef: MatDialogRef<TradeCroppingRecordsPickerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();

    this.header = data.locals.header;
    this.businessId = data.locals.businessId;
    this.tradeStockPeriod = data.locals.tradeStockPeriod;
    this.selectedCroppingRecords = data.locals.croppingRecords;
  }

  get hasSequencedRecords(): boolean {
    return this.croppingRecords.some(cr => cr.sequence > 0);
  }

  private _showSequencesCheckbox = false;
  set showSequencesCheckbox(hasCompleteSequences) {
    this._showSequencesCheckbox =
      this.hasSequencedRecords && hasCompleteSequences;
  }
  get showSequencesCheckbox() {
    return this._showSequencesCheckbox;
  }

  saveTradeCroppingRecords = () =>
    this.tradeCroppingRecordsService
      .saveTradeCroppingRecords(this.header, this.selectedCroppingRecords)
      .then(() => this.close(this.selectedCroppingRecords))
      .catch(() =>
        this.messageService.showMessage(
          MessageTypeEnum.Danger,
          'TRADE_CROPPING_RECORDS_SAVE_ERROR'
        )
      );

  addCroppingRecord(croppingRecord: IPickerCroppingRecord) {
    const cr = _.find(
      this.selectedCroppingRecords,
      scr => scr.croppingRecordId === croppingRecord.croppingRecordId
    );

    if (!cr) {
      const recordToAdd = croppingRecord as ITradeCroppingRecord;
      recordToAdd.allocatedArea = croppingRecord.fieldArea;
      this.selectedCroppingRecords.push(recordToAdd);
      this.tradeCroppingRecordsForm.addCroppingRecord(recordToAdd);
    }
    croppingRecord.selected = true;
  }

  removeCroppingRecord(croppingRecordId: number) {
    _.remove(
      this.selectedCroppingRecords,
      croppingRecord => croppingRecord.croppingRecordId === croppingRecordId
    );

    const cr = _.find(
      this.croppingRecords,
      croppingRecord => croppingRecord.croppingRecordId === croppingRecordId
    );

    cr.selected = false;
  }

  selectAllCroppingRecords() {
    this.fieldSelectorPipe
      .transform(
        this.filteredCroppingRecords.filter(f => !f.selected),
        this.filterText
      )
      .forEach((croppingRecord: IPickerCroppingRecord) =>
        this.addCroppingRecord(croppingRecord)
      );
  }

  removeAllCroppingRecords() {
    this.tradeCroppingRecordsForm.removeAllControls();
    this.selectedCroppingRecords = [];
    this.croppingRecords.forEach(
      croppingRecord => (croppingRecord.selected = false)
    );
  }

  totalSelectedFieldsArea() {
    return _.sumBy(
      this.selectedCroppingRecords,
      croppingRecord => croppingRecord.allocatedArea
    );
  }

  filterChange() {
    this.getCroppingRecords(this.userService.user.filters.fieldPickerTagList);
  }

  userPreferencesModified(event: {
    businesses: ISelectList[];
    fieldGroupTags: ISelectList[];
    landUses: ISelectList[];
    cropTypes: ISelectList[];
    cropVarieties: ISelectList[];
    tags: ISelectList[];
  }) {
    this.selectedBusinessIds = event.businesses
      .filter(o => o.selected)
      .map(o => o.id);

    this.selectedFieldGroupTagIds = event.fieldGroupTags
      .filter(o => o.selected)
      .map(o => o.id);

    this.selectedLandUses = event.landUses
      .filter(o => o.selected)
      .map(o => o.id);

    if (event.cropTypes.filter(o => !o.selected).length === 0) {
      this.selectedCropTypeIds = null;
    } else {
      this.selectedCropTypeIds = event.cropTypes
        .filter(o => o.selected)
        .map(o => o.id);
    }

    if (event.cropVarieties.filter(o => !o.selected).length === 0) {
      this.selectedCropVarietyIds = null;
    } else {
      this.selectedCropVarietyIds = event.cropVarieties
        .filter(o => o.selected)
        .map(o => o.id);
    }

    this.selectedTagIds = event.tags.filter(o => o.selected).map(o => o.id);

    this.getCroppingRecords(this.selectedTagIds);
  }

  private setSelectedCroppingRecords() {
    this.selectedCroppingRecords.forEach(selectedCroppingRecord => {
      const cr = _.find(
        this.croppingRecords,
        croppingRecord =>
          croppingRecord.croppingRecordId ===
          selectedCroppingRecord.croppingRecordId
      );

      if (cr) {
        cr.selected = true;
      }
      this.tradeCroppingRecordsForm.addCroppingRecord(selectedCroppingRecord);
    });
  }

  private getCroppingRecords(tagList: number[]): Promise<any> {
    const seasonId = this.seasonService.displaySeasonForOrganisation();
    return this.croppingRecordsService
      .getPickerCroppingRecords(
        [this.businessId],
        seasonId,
        this.filterText,
        this.tradeStockPeriod,
        false,
        tagList,
        this.selectedFieldGroupTagIds,
        this.selectedLandUses,
        this.selectedCropTypeIds,
        this.selectedCropVarietyIds
      )
      .then((crs: IPickerCroppingRecord[]) => {
        this.croppingRecords = crs;
        this.showSequencesCheckbox = this.croppingRecords.some(
          x => !_.isNil(x.currentCrop) && x.currentCrop
        );

        // If all results when filtered or otherwise result in a blank
        // screen, then toggle the checkbox to include current crop records
        if (
          this.showSequencesCheckbox &&
          this.hideCurrentCrop &&
          this.croppingRecords.every(x => !!x.currentCrop)
        ) {
          this.hideCurrentCrop = !this.hideCurrentCrop;
        }

        this.setFilteredCroppingRecords();
      })
      .then(() => this.setSelectedCroppingRecords())
      .catch(() =>
        this.messageService.showMessage(
          MessageTypeEnum.Danger,
          'PICKER_CROPPING_RECORDS_GET_ERROR'
        )
      );
  }

  toggleCurrentCrop() {
    this.hideCurrentCrop = !this.hideCurrentCrop;
    this.setFilteredCroppingRecords();
  }

  multiTagChange(event: ISelectList[]) {
    return this.userService
      .updateUserMultiFilterPreference(
        'fieldPickerTagList',
        event.filter(o => o.selected).map(o => o.id)
      )
      .then(() => {
        this.getCroppingRecords(
          this.userService.user.filters.fieldPickerTagList
        );
      });
  }

  private setFilteredCroppingRecords() {
    this.filteredCroppingRecords = _.orderBy(
      this.croppingRecords,
      [x => x.fieldName.toLowerCase(), x => x.sequence],
      'asc'
    );

    if (this.showSequencesCheckbox && this.hideCurrentCrop) {
      this.filteredCroppingRecords = this.croppingRecords.filter(
        x => _.isNil(x.currentCrop) || x.currentCrop
      );
    }
  }
}
